import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  AdminPendingBusiness,
  BusinessDecisionInput,
} from "../../interfaces/AdminApprovalObject";
import { ApprovalStatus, PaymentStatus } from "../../interfaces/OnboardingObject";
import { postRequest } from "../http/httpRequest";
import {
  ApiEnvelopeError,
  coerceSingle,
  unwrapEnvelope,
} from "../helpers/ApiEnvelopeHelper";
import { buildBusinessDecisionPayload } from "../converters/uiToBackend/AdminApprovalConvertor";
import {
  convertAdminPendingBusiness,
  convertAdminPendingBusinessList,
} from "../converters/backendToUi/adminApprovalConvertor";

// Admin -> Businesses -> Approvals. Both endpoints are POST + bearer + the
// standard `generalProcessingStatus` envelope.
const BEARER = true;

/** Backend errorCode -> user-facing copy. Codes not listed fall back to the
 *  server's errorMessage (already human-readable per the API contract). */
const FRIENDLY_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "Your session has expired. Please log in again.",
  // Approvals are admin-only server-side. Treated as a STATE by the slice (the
  // tab renders "no access"), not as a failure to retry - same shape as
  // Onboarding's CAPTAIN_NOT_FOUND handling.
  UNAUTHORIZED: "You don't have access to business approvals.",
  ACCESS_DENIED: "You don't have access to business approvals.",
  FORBIDDEN: "You don't have access to business approvals.",
  HTTP_403: "You don't have access to business approvals.",
  PENDING_BUSINESS_NOT_FOUND:
    "This submission couldn't be found. Refresh and try again.",
  // Someone else decided it first, or a double-tap raced.
  BUSINESS_ALREADY_REVIEWED:
    "This submission has already been reviewed. Refresh to see the latest.",
  INVALID_DECISION: "That decision isn't allowed for this submission.",
};

/** Error codes that mean "not allowed to be here", as opposed to "something
 *  broke". The slice turns these into a no-access view. */
const ACCESS_DENIED_CODES = new Set([
  "UNAUTHORIZED",
  "ACCESS_DENIED",
  "FORBIDDEN",
  "HTTP_403",
]);

export function isAccessDeniedError(error: unknown): boolean {
  return (
    error instanceof ApiEnvelopeError && ACCESS_DENIED_CODES.has(error.errorCode)
  );
}

/** Converts any error thrown by this service into a display string. */
export function toFriendlyApprovalError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    return FRIENDLY_MESSAGES[error.errorCode] ?? error.message;
  }
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

/**
 * Every captain's submissions in one approval state.
 *
 * Fetched per status (and cached per status by the slice) rather than fetched
 * once and filtered client-side the way the captain's own screen does: that
 * list is one person's handful of rows, this one is the whole funnel and only
 * grows.
 */
async function listPendingBusinesses(
  approvalStatus: ApprovalStatus
): Promise<AdminPendingBusiness[]> {
  const response = await postRequest(
    ServiceEndpoints.BUSINESS_PENDING_LIST,
    { approvalStatus },
    BEARER
  );
  const contents = await unwrapEnvelope<unknown>(response);
  return convertAdminPendingBusinessList(contents);
}

/**
 * Approve or reject one submission. Returns the UPDATED row, so callers move
 * that row between their per-status caches instead of refetching the list.
 *
 * No idempotency guard server-side that we know of - callers MUST block
 * double-submit while a decision for that pendingId is in flight.
 */
async function decideBusiness(
  input: BusinessDecisionInput
): Promise<AdminPendingBusiness> {
  const response = await postRequest(
    ServiceEndpoints.BUSINESS_DECISION,
    buildBusinessDecisionPayload(input),
    BEARER
  );
  // coerceSingle: this backend array-wraps single objects (legacy builders).
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertAdminPendingBusiness(contents);
}

async function markBusinessPaid(
  pendingId: string,
  note: string
): Promise<{ paymentStatus: PaymentStatus; invoiceUrl: string | null; receiptUrl: string | null }> {
  const url = `${ServiceEndpoints.ONBOARDING_MARK_PAID_BASE}/${pendingId}/mark-paid`;
  const response = await postRequest(url, { note }, BEARER);
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response)) as Record<string, unknown>;
  return {
    paymentStatus: (contents?.paymentStatus as PaymentStatus) ?? "PAID",
    invoiceUrl: (contents?.invoiceUrl as string | null) ?? null,
    receiptUrl: (contents?.receiptUrl as string | null) ?? null,
  };
}

const BusinessApprovalApiService = {
  listPendingBusinesses,
  decideBusiness,
  markBusinessPaid,
};

export default BusinessApprovalApiService;
